// TODO: Look for best way to split, and move to use react table.
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "components/Toast/Toast";
import {
  Accordion,
  AccordionTitle,
  Icon,
  Input,
  Card,
  Checkbox,
  Popup,
  Dropdown,
  Button,
  Label,
  Loader,
} from "semantic-ui-react";
import {
  assetTypeToPlaceholderIcon,
  formatAssetMetadata,
  formatDate,
  getCurrentLanguage,
  getDateFormat,
  showElementFullTextPopup,
  getCountryCode,
  formatInformationLength,
  handleDownloadClick,
  downloadFromUrl,
} from "services/common";
import SemanticDatepicker from "react-semantic-ui-datepickers";
import "react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import CampaignActions from "../CampaignActions/CampaignActions";
import { getCampaignTableState, getGroupedCampaignById } from "store/selectors";
import { CampaignType, AssetStatus } from "models";
import _ from "lodash";
import SummaryModal from "../SummaryModal/SummaryModal";
import CSVSummaryModal from "../CSVSummaryModal/CSVSummaryModal";
import AssetCardInformation from "components/AssetCardInformation/AssetCardInformation";
import AssetImage from "components/AssetImage/AssetImage";
import ExportCampaigns from "services/exportCampaigns";
import { CSVExporter } from "services/apiMiddleware";

const CAP_ASSET_STATUS = ["APPROVED", "REJECTED", "PENDING", "PARTIALLY_REJECTED"];

const RenderCards = React.memo(function RenderCards({ assets, campaign, t }) {
  const getStatusSuffix = (status) => {
    let statusSuffix;
    const isQCStatus = status.includes("QC") || status.includes("ZIP");
    const isUploadFailed = status.includes("ARRIVED");

    if (status) {
      if (isQCStatus) {
        statusSuffix = status.toUpperCase().split("_")[1];
      } else if (isUploadFailed) {
        statusSuffix = "UPLOAD FAILED";
      } else {
        statusSuffix = status;
      }
      return statusSuffix;
    }
  };

  return assets.map((asset, i) => {
    const status = asset.status;
    const statusSuffix = getStatusSuffix(status);
    const color =
      status === AssetStatus.QC_FAILED ||
      status === AssetStatus.REJECTED ||
      status === AssetStatus.ARRIVED
        ? "red"
        : status === AssetStatus.PARTIALLY_REJECTED
        ? "orange"
        : status === AssetStatus.QC_CAUTION || status === AssetStatus.PENDING
        ? "yellow"
        : status === AssetStatus.QC_OK ||
          status === AssetStatus.APPROVED ||
          status === AssetStatus.ZIP_OK
        ? "green"
        : undefined;
    return (
      <Card color={color} key={i}>
        <Card.Content
          style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              maxHeight: "150px",
              overflow: "hidden",
            }}
          >
            <AssetImage asset={asset} />
          </div>
          <Card.Header style={{ marginTop: "1rem", wordBreak: "break-all" }}>
            <div className="material-type">
              {assetTypeToPlaceholderIcon(asset.type, "large")}
              <Popup
                content={asset.name}
                position="top center"
                disabled={!asset.name || asset.name.length <= 18}
                trigger={<span>{formatInformationLength(asset.name, 18)}</span>}
              />
            </div>
          </Card.Header>
          <AssetCardInformation asset={asset} maxLength={18} t={t} />
          {status !== "ARRIVED" && (
            <a
              href={asset.mediaUrl}
              title={t("campaigns_download_link_title", { assetName: asset.name })}
              onClick={(e) => handleDownloadClick(e, asset.mediaUrl, t)}
            >
              <Icon name="cloud download" size="large" /> {t("asset_download")}
            </a>
          )}
        </Card.Content>
        <Card.Content extra>
          <Popup
            disabled={CAP_ASSET_STATUS.includes(asset.status)}
            content={formatAssetMetadata(asset.metadata, campaign.duration || [])}
            trigger={
              <div key={asset.id} className="asset-title-container">
                <div className={`status ${asset.status.toLowerCase()}`} key={asset.id}>
                  {asset.status.toUpperCase().indexOf("QC") > -1 ||
                  [
                    "PENDING",
                    "APPROVED",
                    "REJECTED",
                    "PARTIALLY_REJECTED",
                    "ZIP_OK",
                    "ARRIVED",
                  ].includes(asset.status) ? (
                    <div
                      style={{
                        textAlign: "center",
                        position: "relative",
                        top: "8px",
                      }}
                    >
                      {statusSuffix === "FAILED" ||
                      statusSuffix === "REJECTED" ||
                      statusSuffix === "PARTIALLY_REJECTED" ||
                      statusSuffix === "UPLOAD FAILED" ? (
                        <Icon color={color} name="x" />
                      ) : statusSuffix === "CAUTION" || statusSuffix === "PENDING" ? (
                        <Icon color={color} name="warning" />
                      ) : (
                        <Icon color={color} name="check" />
                      )}
                      {statusSuffix.replace("_", " ")}
                    </div>
                  ) : (
                    asset.status.toUpperCase()
                  )}
                </div>
              </div>
            }
            position="top center"
          />
        </Card.Content>
      </Card>
    );
  });
});

const RenderAssets = ({ assets: assetsProp, campaign, t }) => {
  const assets = useMemo(() => assetsProp || [], [assetsProp]);

  const carouselItemsLength = Math.floor(Math.abs(assets.length / 8) + 1);
  const assetsSlice = Array.from(Array(carouselItemsLength), (x, index) => 0 + index * 8);
  const campaignAssetsCarousel = useMemo(() => {
    let campaignAssets = [];
    for (let i = 0; i < assetsSlice.length; i++) {
      let superiorLimit = assetsSlice[i] + 8 < assets.length ? assetsSlice[i] + 8 : assets.length;
      campaignAssets.push(assets.slice(assetsSlice[i], superiorLimit));
    }
    return campaignAssets;
  }, [assets, assetsSlice]);

  if (assets.length <= 8) {
    return (
      <div className="minimum-assets">
        <Card.Group itemsPerRow={4}>
          <RenderCards assets={assets} campaign={campaign} t={t} />
        </Card.Group>
      </div>
    );
  }

  return (
    <Carousel showThumbs={false}>
      {campaignAssetsCarousel.map((subArray, index) => {
        return (
          <div key={index}>
            <Card.Group itemsPerRow={4}>
              <RenderCards assets={subArray} campaign={campaign} t={t} />
            </Card.Group>
          </div>
        );
      })}
    </Carousel>
  );
};

const CampaignAssets = ({ loading, assets, campaign, t }) => {
  return loading ? (
    <Loader active={loading} inline="centered" size="small">
      {t("loading")}
    </Loader>
  ) : (
    <RenderAssets assets={assets} campaign={campaign} t={t} />
  );
};

const LoadAssetsAsync = React.memo(function LoadAssetsAsync({ campaign, t }) {
  const dispatch = useDispatch();
  const campaignId = campaign.id;

  useEffect(() => {
    dispatch.assets.loadCampaignAssets(campaignId);
  }, [dispatch, campaignId]);

  const { assets, isLoading } =
    useSelector((state) => getGroupedCampaignById(state, { campaignId })) || {};

  return <CampaignAssets loading={isLoading} assets={assets} campaign={campaign} t={t} />;
});

const Campaigns = ({
  groups,
  maxNrOfMergeCampaigns,
  currentPageKey,
  staticValues,
  accordionKey,
  campaignsForMerge,
  isLazyLoading = false,
  t,
}) => {
  const dispatch = useDispatch();
  const providedDataKeys = useMemo(() => Object.keys(groups[0]), [groups]);
  const tableState = useSelector(getCampaignTableState);
  const [searchStatus, setSearchStatus] = useState(tableState);
  const [deliveryDeadlineFilter, setDeliveryDeadlineFilter] = useState({
    filterOpen: false,
    range: null,
  });
  const [orderIndexKey] = useState(searchStatus.orderBy.key);
  const orderIndexFound = useMemo(
    () => providedDataKeys.findIndex((key) => key === orderIndexKey),
    [orderIndexKey, providedDataKeys]
  );
  const [orderIndexDirection] = useState(searchStatus.orderBy.direction);
  const [campaignsElements, setCampaignsElements] = useState(groups);

  const printCampaignStatus = [
    "Awaiting delivery",
    "Partially delivered",
    "Delivered",
    "Copy missing",
    "Checked",
    "Sent to print",
    "API Error",
    "API Pending",
    "API Approved",
    "API Partially Rejected",
    "API Rejected",
    "Approved",
    "Rejected",
  ];

  const digitalCampaignStatus = [
    "Awaiting delivery",
    "Partially delivered",
    "Delivered",
    "Copy missing",
    "Checked",
    "Scheduled",
    "CAP Approved",
    "CAP Rejected",
    "CAP Pending",
    "CAP Partially Rejected",
    "ZIP Delivered",
    "Approved",
    "Rejected",
  ];

  const statusValues = () => {
    const values =
      currentPageKey === "campaigns"
        ? [...new Set([...printCampaignStatus, ...digitalCampaignStatus])]
        : ["Cancelled", "Archived"];
    return values;
  };
  const [campaignStatusValues] = useState(statusValues);
  const { campaignsToBeMerged } = useSelector((state) => state.campaignsPage);
  const [selectedMergeCampaigns, setSelectedMergeCampaigns] = useState(campaignsToBeMerged);
  const campaignOrderlineIDRef = useRef();
  const campaignAdvertiserRef = useRef();
  const campaignNameRef = useRef();
  const campaignContractNumberRef = useRef();
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [viewSummary, setViewSummary] = useState([]);
  const [CSVsummaryModalOpen, setCSVSummaryModalOpen] = useState(false);
  const [CSVviewSummary, setCSVViewSummary] = useState([]);

  useEffect(() => {
    if (selectedMergeCampaigns !== campaignsToBeMerged)
      dispatch.campaignsPage.setMergeCampaigns(selectedMergeCampaigns);
  }, [selectedMergeCampaigns, campaignsToBeMerged]);

  const useSortedItems = (items) => {
    const [sortingItemConfig, setSortingItemConfig] = useState({
      key: providedDataKeys[orderIndexFound],
      direction: orderIndexDirection,
    });

    const sortedItems = useMemo(() => {
      let sortableItems = [...items];
      if (sortingItemConfig !== null) {
        sortableItems.sort((a, b) => {
          if (a[sortingItemConfig.key] !== null && b[sortingItemConfig.key] !== null) {
            if (a[sortingItemConfig.key] < b[sortingItemConfig.key]) {
              return sortingItemConfig.direction === "ascending" ? -1 : 1;
            }
            if (a[sortingItemConfig.key] > b[sortingItemConfig.key]) {
              return sortingItemConfig.direction === "ascending" ? 1 : -1;
            }
            return 0;
          } else {
            const noData =
              a[sortingItemConfig.key] === null ? 1 : b[sortingItemConfig.key] === null ? -1 : 0;
            return noData;
          }
        });
      }
      return sortableItems;
    }, [items, sortingItemConfig]);

    const sortItemsBy = (key) => {
      let direction = "ascending";
      if (
        sortingItemConfig &&
        sortingItemConfig.key === key &&
        sortingItemConfig.direction === "ascending"
      ) {
        direction = "descending";
      }
      setSortingItemConfig({ key, direction });
    };

    return {
      items: sortedItems,
      sortItemsBy,
      sortingItemConfig,
    };
  };

  let { items, sortItemsBy, sortingItemConfig } = useSortedItems(campaignsElements);
  const [rowsPerPage, setRowsPerPage] = useState({ value: "25", label: "25" });

  const currentPageValue = !staticValues ? parseInt(tableState.currentPage.value) : 1;
  const rowsPerPageValue = rowsPerPage && parseInt(rowsPerPage.value);
  const lastRowIndex = currentPageValue * rowsPerPageValue;
  const firstRowIndex = lastRowIndex - rowsPerPageValue;
  let currentRows = items.slice(firstRowIndex, lastRowIndex);
  const statusFilterValues = statusValues().reduce((acc, curr) => {
    acc[curr] = false;
    return acc;
  }, {});

  const pageNumbers = useMemo(() => {
    const pagesList = [];
    for (let i = 1; i <= Math.ceil(items.length / rowsPerPageValue); i++) {
      pagesList.push({ value: `${i}`, text: `${i}` });
    }
    return pagesList;
  }, [items, rowsPerPageValue]);

  const [activeCampaignIndex, setActiveCampaignIndex] = useState([]);
  const withReset = (handler) => (e, data) => {
    setActiveCampaignIndex([]);
    return handler?.(e, data);
  };

  useEffect(() => {
    let data = groups;
    //handleSetCurrentPage({ value: "1", text: "1" });
    Object.entries(searchStatus).forEach(([key, value]) => {
      if (key === "type") {
        const types = Object.keys(searchStatus.type.index);
        const searchTypeIndex = [];
        types.map((type) => {
          searchStatus.type.index[type] && searchTypeIndex.push(type);
          return type;
        });
        if (searchTypeIndex.length === 1) {
          data = data.filter((campaignType) => {
            return campaignType.type === CampaignType[searchTypeIndex[0].toUpperCase()];
          });
        }
      } else if (key === "status") {
        const status = Object.keys(searchStatus.status.index);
        const searchStatusIndex = [];
        status.map((statusValue) => {
          searchStatus.status.index[statusValue] && searchStatusIndex.push(statusValue);
          return statusValue;
        });

        if (searchStatusIndex.length > 0) {
          const statusFilterResults = () =>
            searchStatusIndex.map((status) => {
              let occurrences = data.filter((campaign) => {
                if (campaign && campaign.status) {
                  return (
                    campaign.status.split("_").join(" ").toLowerCase() === status.toLowerCase()
                  );
                }
                return false;
              });
              return occurrences;
            });

          const joinStatusFilterResults = (statusFilterResults) =>
            statusFilterResults.reduce(
              (prevResult, nextResult) =>
                prevResult.concat(
                  Array.isArray(nextResult)
                    ? [...new Set(joinStatusFilterResults(nextResult))]
                    : nextResult
                ),
              []
            );

          const statusResult = joinStatusFilterResults(statusFilterResults());
          data = statusResult;
        }
      } else if (key === "orderlineID" || key === "advertiser" || key === "name") {
        if (searchStatus[key].searchTerm !== "") {
          const result = data.filter((result) => {
            if (result[key]) {
              return result[key].toLowerCase().includes(searchStatus[key].searchTerm.toLowerCase());
            }
            return false;
          });
          data = result;
        }
      } else if (key === "globalSearch" && searchStatus.globalSearch.searchTerm !== "") {
        const searchData = data;
        const genericSearch = (searchCriteria) => {
          const findMatches = (searchData) => {
            if (searchData && searchCriteria) {
              if (
                searchData === searchCriteria ||
                (searchData.toString() &&
                  searchCriteria.toString() &&
                  searchData
                    .toString()
                    .toLowerCase()
                    .includes(searchCriteria.toString().toLowerCase()))
              )
                return true;

              return (
                searchData === Object(searchData) && Object.values(searchData).some(findMatches)
              );
            }
          };

          return (Array.isArray(searchData) ? searchData : Object.values(searchData)).filter(
            findMatches
          );
        };

        data = genericSearch(searchStatus.globalSearch.searchTerm);
      }
    });

    if (Array.isArray(deliveryDeadlineFilter.range) && deliveryDeadlineFilter.range.length > 0) {
      const fromRaw = deliveryDeadlineFilter.range[0] || deliveryDeadlineFilter.range[1];
      const toRaw = deliveryDeadlineFilter.range[1] || deliveryDeadlineFilter.range[0];

      if (!fromRaw && !toRaw) {
        setCampaignsElements(data);
        return;
      }

      const from = new Date(fromRaw);
      from.setHours(0, 0, 0, 0);

      const to = new Date(toRaw);
      to.setHours(23, 59, 59, 999);

      data = data.filter((delivery) => {
        const deadlineValue =
          delivery?.deliveryDeadline ?? delivery?.delivery?.deliveryDeadline ?? null;

        if (!deadlineValue) return false;

        const deadline = new Date(deadlineValue);
        if (Number.isNaN(deadline.getTime())) return false;

        return deadline >= from && deadline <= to;
      });
    }

    setCampaignsElements(data);
  }, [
    groups,
    searchStatus.status.index,
    searchStatus.type.index,
    searchStatus.globalSearch.searchTerm,
    searchStatus.name.searchTerm,
    searchStatus.orderlineID.searchTerm,
    searchStatus.advertiser.searchTerm,
    searchStatus.statusChange,
    deliveryDeadlineFilter.range && deliveryDeadlineFilter.range[0],
    deliveryDeadlineFilter.range && deliveryDeadlineFilter.range[1],
  ]);

  const handleTabChange = () => {
    // disabling eslint because hasOwnProperty only touches objects with known keys and
    // never use an object that shadows an Object.prototype method or
    // which does not inherit from Object.prototype.
    if (
      currentPageKey === "inactive-campaigns" &&
      // eslint-disable-next-line no-prototype-builtins
      searchStatus.status.index.hasOwnProperty("Scheduled")
    ) {
      dispatch.campaignTable.updateNavigation(false);
      !tableState.previousPage && handleClearFilters();
      if (!tableState.previousPage) {
        handleClearFilters();
        dispatch.campaignTable.updateNavigation(false);
      }
    } else if (
      currentPageKey === "campaigns" &&
      // eslint-disable-next-line no-prototype-builtins
      searchStatus.status.index.hasOwnProperty("Archived")
    ) {
      handleClearFilters();
    }
  };

  useEffect(() => {
    handleTabChange();
  }, [currentPageKey]);

  useEffect(() => {
    dispatch.approvers.load();
  }, []);

  // Store searchStatus-object to redux
  // - so filters not be cleared if user navigates other pages
  useEffect(() => {
    dispatch.campaignTable.updateSearchState(searchStatus);
  }, [searchStatus]);

  const handleUpdateTableOrder = (orderConfig) => {
    setSearchStatus((prevSearchStatus) => ({
      ...prevSearchStatus,
      orderBy: orderConfig,
    }));
  };

  useEffect(() => handleUpdateTableOrder(sortingItemConfig), [sortingItemConfig]);

  const handleSetCurrentPage = (newPageValues) => {
    setSearchStatus((prevSearchStatus) => ({
      ...prevSearchStatus,
      currentPage: newPageValues,
    }));
  };

  const handleFilterUpdate = (filter, updatedValue) => {
    setActiveCampaignIndex([]);
    handleSetCurrentPage({ value: "1", text: "1" });
    setSearchStatus((prevSearchStatus) => ({
      ...prevSearchStatus,
      [filter]: {
        ...prevSearchStatus[filter],
        index: {
          ...prevSearchStatus[filter].index,
          [updatedValue]: !prevSearchStatus[filter].index[updatedValue],
        },
      },
    }));
  };

  const handleDeleteCampaign = async (campaign) => {
    if (campaign)
      try {
        await dispatch.campaigns.delete(campaign);
        dispatch.assets.removeCampaign(campaign.id);
        toast(undefined, "Campaign deleted successfully!");
      } catch (error) {
        console.error(error);
        toast("error", "Campaign deletion failed: " + error.message);
      }
  };

  const handleCampaignClick = (e, data) => {
    const { itemindex } = data;
    activeCampaignIndex.indexOf(itemindex) > -1
      ? setActiveCampaignIndex([])
      : setActiveCampaignIndex([itemindex]);
  };

  const getSortingDirectionIcon = (header) => {
    if (!sortingItemConfig) {
      return (
        <>
          <Icon name="angle up" />
          <Icon name="angle down" />
        </>
      );
    }
    return sortingItemConfig.key === header && sortingItemConfig.direction === "descending" ? (
      <>
        <Icon name="angle up" style={{ opacity: ".5" }} />
        <Icon name="angle down" />
      </>
    ) : sortingItemConfig.key === header && sortingItemConfig.direction === "ascending" ? (
      <>
        <Icon name="angle up" />
        <Icon name="angle down" style={{ opacity: ".5" }} />
      </>
    ) : (
      <>
        <Icon name="angle up" /> <Icon name="angle down" />
      </>
    );
  };

  const handleCampaignCheckboxClicked = (e, target) => {
    e.stopPropagation();
    const delivery = target.delivery;
    if (target.checked) {
      setSelectedMergeCampaigns(_.concat(selectedMergeCampaigns, delivery));
    } else {
      setSelectedMergeCampaigns(
        _.filter(selectedMergeCampaigns, (campaign) => delivery.id !== campaign.id)
      );
    }
  };

  const campaignCheckboxIsDisabled = (campaign) => {
    /* Campaign's checkbox is not disabled if the number of selected campaigns is less than 
       the maximum number defined or the campaign is selected. Otherwise checkboxes are disabled.
    */
    if (selectedMergeCampaigns.length < maxNrOfMergeCampaigns) return false;
    if (_.includes(_.map(selectedMergeCampaigns, "id"), campaign.id)) return false;
    return true;
  };

  const renderCampaignStatus = (campaign, newCampaignStatus) => {
    items.find(({ id }) => id === campaign.id).status = newCampaignStatus;
    setSearchStatus((prevSearchStatus) => ({
      ...prevSearchStatus,
      statusChange: (prevSearchStatus.statusChange += 1),
    }));
  };
  const setNavigationToCampaignPage = () => {
    dispatch.campaignTable.updateNavigation(true);
  };

  const handleClearFilters = () => {
    setActiveCampaignIndex([]);
    setCampaignsElements(groups);
    sortItemsBy("");
    setDeliveryDeadlineFilter({ filterOpen: false, range: null });
    setSearchStatus({
      ...searchStatus,
      advertiser: { searchOpen: false, searchTerm: "" },
      globalSearch: { searchTerm: "" },
      orderlineID: { searchOpen: false, searchTerm: "" },
      name: { searchOpen: false, searchTerm: "" },
      type: { filterOpen: false, index: { digital: false, print: false } },
      status: {
        filterOpen: false,
        index: statusFilterValues,
      },
      orderBy: { key: undefined, direction: "" },
      currentPage: { value: "1", text: "1" },
      statusChange: 0,
    });
  };

  const handleIsFilterOpen = (filterName, boolValue) => {
    setSearchStatus((prevSearchStatus) => ({
      ...prevSearchStatus,
      [filterName]: { ...prevSearchStatus[filterName], filterOpen: boolValue },
    }));
  };

  const handleCreateSummary = (data) => {
    const summary = data.map((campaign) => {
      const resolutions = campaign.requiredResolutions.items.map((resolution) => {
        return {
          type: resolution.requiredResolution.type,
          resolution: resolution.requiredResolution.value,
        };
      });
      return {
        title: `${campaign.advertiser} / ${campaign.name}`,
        orderlineID: campaign.orderlineID,
        requiredResolutions: resolutions,
        deliveryDeadline: campaign.deliveryDeadline,
        contract_number: campaign.contract_number,
        type: campaign.type,
        starts: campaign.start_time,
        ends: campaign.end_time,
      };
    });
    setViewSummary(summary);
    setSummaryModalOpen(true);
  };

  const handleCreateCSVSummary = (data) => {
    const summary = data.map((campaign) => {
      return {
        title: `${campaign.advertiser} / ${campaign.name}`,
        id: campaign.id,
      };
    });
    setCSVViewSummary(summary);
    setCSVSummaryModalOpen(true);
  };

  const downloadCSV = async (downloadUrl) => {
    if (!downloadUrl) return false;
    try {
      await downloadFromUrl(downloadUrl);
      return true;
    } catch (error) {
      return false;
    }
  };

  const renderHeader = () => {
    let downloadUrl;
    return (
      <div className="accordion-headers">
        <SummaryModal
          data={viewSummary}
          open={summaryModalOpen}
          setOpen={setSummaryModalOpen}
          onSuccess={(data) => {
            const success = ExportCampaigns(data.campaigns, data.siteVersion);
            if (success) {
              toast("success", t("export-toast-success"));
            } else {
              toast("error", t("export-toast-error"));
            }
          }}
          dataForOnSuccess={{
            campaigns: viewSummary,
            siteVersion: getCountryCode(),
          }}
          t={t}
        />
        <CSVSummaryModal
          data={CSVviewSummary}
          open={CSVsummaryModalOpen}
          setOpen={setCSVSummaryModalOpen}
          onSuccess={async (data) => {
            downloadUrl = await CSVExporter(data.campaigns);
            const success = await downloadCSV(downloadUrl);
            if (success) {
              toast("success", t("export-toast-success"));
            } else {
              toast("error", t("export-toast-error"));
            }
          }}
          dataForOnSuccess={{
            campaigns: CSVviewSummary,
            siteVersion: getCountryCode(),
          }}
          t={t}
        />
        <ul>
          <li></li>
          <li>
            <button type="button" onClick={withReset(() => sortItemsBy("contract_number"))}>
              {t("campaign_contract_number")}
              <div className="sorting-arrows">{getSortingDirectionIcon("contract_number")}</div>
            </button>
          </li>
          <li>
            <button
              type="button"
              disabled={isLazyLoading}
              onClick={() => toggleSearchBoxVisibility("orderlineID")}
              style={{ display: !searchStatus.orderlineID.searchOpen ? "flex" : "none" }}
            >
              OrderlineID <Icon name="search" />
            </button>
            <div
              className="ui icon input"
              style={{ display: searchStatus.orderlineID.searchOpen ? "flex" : "none" }}
            >
              <Input
                type="search"
                disabled={isLazyLoading}
                name="orderlineID"
                value={searchStatus.orderlineID.searchTerm}
                onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                style={{ width: "99px" }}
                action={{
                  style: { backgroundColor: "transparent" },
                  icon: "close",
                  onClick: () => toggleSearchBoxVisibility("orderlineID"),
                }}
                placeholder="OrderlineID"
              />
            </div>
          </li>
          <li>
            <button
              type="button"
              disabled={isLazyLoading}
              onClick={() => toggleSearchBoxVisibility("advertiser")}
              style={{ display: !searchStatus.advertiser.searchOpen ? "flex" : "none" }}
            >
              {t("campaign_advertiser")} <Icon name="search" />
            </button>
            <div
              className="ui icon input"
              style={{ display: searchStatus.advertiser.searchOpen ? "flex" : "none" }}
            >
              <Input
                type="text"
                disabled={isLazyLoading}
                name="advertiser"
                value={searchStatus.advertiser.searchTerm}
                onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                action={{
                  style: { backgroundColor: "transparent" },
                  icon: "close",
                  onClick: () => toggleSearchBoxVisibility("advertiser"),
                }}
                placeholder={t("campaign_advertiser")}
              />
            </div>
          </li>
          <li>
            <button
              type="button"
              disabled={isLazyLoading}
              onClick={() => toggleSearchBoxVisibility("name")}
              style={{ display: !searchStatus.name.searchOpen ? "flex" : "none" }}
            >
              {t("campaign_name")} <Icon name="search" />
            </button>
            <div
              className="ui icon input"
              style={{ display: searchStatus.name.searchOpen ? "flex" : "none" }}
            >
              <Input
                type="text"
                disabled={isLazyLoading}
                name="name"
                value={searchStatus.name.searchTerm}
                onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                style={{ border: "1px solid rgba(34,36,38,.15) !important" }}
                action={{
                  style: { backgroundColor: "transparent" },
                  icon: "close",
                  onClick: () => toggleSearchBoxVisibility("name"),
                }}
                placeholder={t("campaign_name")}
              />
            </div>
          </li>
          <li>
            <Popup
              pinned
              style={{ maxWidth: "400px" }}
              content={
                <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
                  <strong>{t("campaign_deliveryDeadline")}</strong>

                  <SemanticDatepicker
                    id="deliveryDeadlineRange"
                    type="range"
                    className="delivery-deadline-range-picker"
                    value={deliveryDeadlineFilter.range}
                    onChange={(_e, data) =>
                      setDeliveryDeadlineFilter((prev) => ({
                        ...prev,
                        range: Array.isArray(data.value) ? data.value : null,
                      }))
                    }
                    firstDayOfWeek={1}
                    format={getDateFormat()}
                    autoComplete="off"
                    locale={getCurrentLanguage()}
                    clearable
                  />

                  <div style={{ display: "flex", gap: ".5rem" }}>
                    <Button
                      basic
                      size="small"
                      onClick={() =>
                        setDeliveryDeadlineFilter((prev) => ({ ...prev, range: null }))
                      }
                    >
                      {t("clear")}
                    </Button>
                  </div>
                </div>
              }
              trigger={
                <Button
                  as="div"
                  style={{ padding: "0", backgroundColor: "transparent", color: "black" }}
                  labelPosition="left"
                  onClick={() => setActiveCampaignIndex([])}
                >
                  <Label
                    as="a"
                    style={{
                      borderColor: "none !important",
                      backgroundColor: "transparent",
                      color: "black",
                      padding: "0 .3rem",
                    }}
                  >
                    {t("campaign_deliveryDeadline")}
                  </Label>

                  <Button icon style={{ padding: "0", background: "transparent" }}>
                    <Icon name="filter" style={{ color: "black" }} />
                  </Button>
                </Button>
              }
              onOpen={() => setDeliveryDeadlineFilter((prev) => ({ ...prev, filterOpen: true }))}
              onClose={() => setDeliveryDeadlineFilter((prev) => ({ ...prev, filterOpen: false }))}
              on="click"
              open={deliveryDeadlineFilter.filterOpen}
            />
          </li>
          <li>
            <button type="button" onClick={withReset(() => sortItemsBy("start_time"))}>
              {t("campaign_start_time")}
              <div className="sorting-arrows">{getSortingDirectionIcon("start_time")}</div>
            </button>
          </li>
          <li>
            <button type="button" onClick={withReset(() => sortItemsBy("end_time"))}>
              {t("campaign_end_time")}
              <div className="sorting-arrows">{getSortingDirectionIcon("end_time")}</div>
            </button>
          </li>
          <li>
            <Popup
              pinned
              style={{ maxWidth: "400px" }}
              content={
                <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
                  <strong>{t("campaigns_filter_by_type")}</strong>
                  <Checkbox
                    checked={searchStatus.type.index.digital}
                    label="Digital"
                    onClick={(e) => handleFilterUpdate("type", e.target.innerText.toLowerCase())}
                  />
                  <Checkbox
                    checked={searchStatus.type.index.print}
                    label="Print"
                    onClick={(e) => handleFilterUpdate("type", e.target.innerText.toLowerCase())}
                  />
                </div>
              }
              trigger={
                <Button
                  as="div"
                  style={{ padding: "0", color: "black" }}
                  labelPosition="left"
                  onClick={() => setActiveCampaignIndex([])}
                >
                  <Label
                    as="a"
                    style={{
                      borderColor: "none !important",
                      backgroundColor: "transparent",
                      color: "black",
                      padding: "0 .3rem",
                    }}
                  >
                    {t("asset_type")}
                  </Label>
                  <Button icon style={{ padding: "0", background: "transparent" }}>
                    <Icon name="filter" style={{ color: "black" }} />
                  </Button>
                </Button>
              }
              onOpen={() => handleIsFilterOpen("type", true)}
              onClose={() => handleIsFilterOpen("type", false)}
              on="click"
              open={searchStatus.type.filterOpen}
            />
          </li>
          <li>
            <Popup
              pinned
              style={{ maxWidth: "400px" }}
              content={
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: ".5rem",
                    maxHeight: "220px",
                    width: "auto",
                    overflow: "scroll",
                    overflowX: "hidden",
                    height: "auto",
                  }}
                >
                  <strong>{t("campaigns_filter_by_status")}</strong>
                  {campaignStatusValues.map((statusValue, i) => {
                    return (
                      <Checkbox
                        checked={
                          searchStatus.status.index && searchStatus.status.index[statusValue]
                        }
                        key={i}
                        label={statusValue}
                        value={statusValue}
                        onClick={() => handleFilterUpdate("status", statusValue)}
                      />
                    );
                  })}
                </div>
              }
              trigger={
                <Button
                  as="div"
                  style={{ padding: "0", backgroundColor: "transparent", color: "black" }}
                  labelPosition="left"
                  onClick={() => setActiveCampaignIndex([])}
                >
                  <Label
                    as="a"
                    style={{
                      borderColor: "none !important",
                      backgroundColor: "transparent",
                      color: "black",
                      padding: "0 .3rem",
                    }}
                  >
                    {t("asset_status")}
                  </Label>
                  <Button icon style={{ padding: "0", background: "transparent" }}>
                    <Icon name="filter" style={{ color: "black" }} />
                  </Button>
                </Button>
              }
              onOpen={() => handleIsFilterOpen("status", true)}
              onClose={() => handleIsFilterOpen("status", false)}
              on="click"
              open={searchStatus.status.filterOpen}
            />
          </li>
          <li></li>
        </ul>
      </div>
    );
  };

  const renderItems = () => {
    if (campaignsForMerge) {
      currentRows = [...new Set([...currentRows, ...groups])];
    }
    let items = currentRows.map((delivery, i) => ({
      key: accordionKey ? `${delivery.id}-${accordionKey}` : delivery.id,
      title: (
        <AccordionTitle
          className={delivery.status ? delivery.status.toLowerCase() : "null"}
          data-test={`accordion_title_${delivery.name}`}
          itemindex={i}
        >
          <div key={i} className="campaign-row-information">
            <ul>
              <li>
                <Checkbox
                  onChange={handleCampaignCheckboxClicked}
                  delivery={delivery}
                  disabled={campaignCheckboxIsDisabled(delivery)}
                  checked={_.includes(_.map(selectedMergeCampaigns, "id"), delivery.id)}
                />
              </li>
              <li ref={campaignContractNumberRef}>
                {showElementFullTextPopup(
                  campaignContractNumberRef,
                  delivery.contract_number,
                  "bold 1rem Lato"
                )}
              </li>
              <li ref={campaignOrderlineIDRef}>
                {showElementFullTextPopup(
                  campaignOrderlineIDRef,
                  delivery.orderlineID,
                  "bold 1rem Lato"
                )}
              </li>
              <li ref={campaignAdvertiserRef}>
                {showElementFullTextPopup(
                  campaignAdvertiserRef,
                  delivery.advertiser,
                  "bold 1rem Lato"
                )}
              </li>
              <li ref={campaignNameRef}>
                {showElementFullTextPopup(campaignNameRef, delivery.name, "bold 1rem Lato")}
              </li>
              <li>{formatDate(delivery.deliveryDeadline)}</li>
              <li>{formatDate(delivery.start_time)}</li>
              <li>{formatDate(delivery.end_time)}</li>
              <li>{delivery.type}</li>
              <li>{delivery.status ? delivery.status.replace(/_/g, " ").split() : "---"}</li>
              <Icon className="caret right" />
            </ul>
          </div>
        </AccordionTitle>
      ),
      content: {
        content: (
          <>
            <CampaignActions
              delivery={delivery}
              handleDeleteCampaign={handleDeleteCampaign}
              setNavigationToCampaignPage={setNavigationToCampaignPage}
              renderCampaignStatus={renderCampaignStatus}
            />
            {activeCampaignIndex?.[0] === i && (
              <div className="assets-row">
                <LoadAssetsAsync campaign={delivery} t={t} />
              </div>
            )}
          </>
        ),
      },
    }));
    return items;
  };

  const handleInputChange = (inputName, searchValue) => {
    setActiveCampaignIndex([]);
    setSearchStatus((prevSearchStatus) => ({
      ...prevSearchStatus,
      [inputName]: { ...prevSearchStatus[inputName], searchTerm: searchValue },
    }));
  };

  const setTablePage = (direction) => {
    setActiveCampaignIndex([]);
    if (direction === "back" && currentPageValue > 1) {
      handleSetCurrentPage({
        value: `${currentPageValue - 1}`,
        text: `${currentPageValue - 1}`,
      });
    }
    if (direction === "forward" && currentPageValue < pageNumbers.length) {
      handleSetCurrentPage({
        value: `${currentPageValue + 1}`,
        text: `${currentPageValue + 1}`,
      });
    }
  };

  const rowsPerPageValues = [
    { value: "15", text: "15" },
    { value: "25", text: "25" },
    { value: "35", text: "35" },
    { value: "45", text: "45" },
    { value: "55", text: "55" },
  ];

  const toggleSearchBoxVisibility = (searchBoxName) => {
    setActiveCampaignIndex([]);
    setSearchStatus({
      ...searchStatus,
      [searchBoxName]: { searchOpen: !searchStatus[searchBoxName].searchOpen, searchTerm: "" },
    });
  };

  return (
    <div className="page-container">
      {!campaignsForMerge && (
        <div className="search-filter" style={{ display: "flex", gap: "1rem" }}>
          <div>
            <Button basic className="bmo-purple" onClick={() => handleClearFilters()}>
              <Icon name="remove" />
              {t("campaigns_page_clear_filters")}
            </Button>
            <Button
              basic
              className="bmo-purple"
              onClick={() => handleCreateSummary(campaignsElements)}
            >
              <Icon name="file pdf" />
              {t("export_campaigns")}
            </Button>
            <Button
              basic
              className="bmo-purple"
              onClick={() => handleCreateCSVSummary(campaignsElements)}
            >
              <Icon name="file excel" />
              {t("export_CSV_campaigns")}
            </Button>
          </div>
          <Input
            icon="search"
            disabled={isLazyLoading}
            placeholder={t("search")}
            name="globalSearch"
            value={searchStatus.globalSearch.searchTerm}
            onChange={(e) => handleInputChange(e.target.name, e.target.value)}
            style={{ marginBottom: "1rem", marginLeft: "auto" }}
          />
        </div>
      )}
      {!campaignsForMerge && renderHeader()}
      {currentRows && currentRows.length !== 0 ? (
        <div>
          {
            <>
              <Accordion
                style={{ width: "100%" }}
                key={accordionKey ? accordionKey : 0}
                panels={renderItems()}
                styled
                exclusive={false}
                activeIndex={activeCampaignIndex}
                onTitleClick={(e, data) => handleCampaignClick(e, data)}
              />
              {!campaignsForMerge && (
                <div className="pagination-row">
                  <div>{t("campaigns_page_pagination_campaigns_per_page")}</div>
                  <Dropdown
                    text={rowsPerPage.value}
                    onChange={(e, { text, value }) => {
                      setRowsPerPage({ value: value, text: value });
                      handleSetCurrentPage({ value: "1", text: "1" });
                    }}
                    compact
                    selection
                    options={rowsPerPageValues}
                  />
                  <div className="items-information">
                    {`${
                      currentPageValue === 1
                        ? currentPageValue
                        : rowsPerPageValue * currentPageValue - rowsPerPageValue + 1
                    } - ${lastRowIndex <= items.length ? lastRowIndex : items.length} / ${
                      items.length
                    } ${t("campaigns_page_pagination_campaigns")}`}
                  </div>
                  <div>{`${t("page")}:`}</div>
                  <Dropdown
                    text={tableState.currentPage.text}
                    onChange={(e, { text, value }) =>
                      handleSetCurrentPage({ value: value, text: value })
                    }
                    compact
                    selection
                    options={pageNumbers}
                  />
                  <div className="number-of-pages">
                    {t("campaigns_number_of_pages", {
                      currentPageValue: currentPageValue,
                      pageNumbersLength: pageNumbers.length,
                    })}
                  </div>
                  <div className="arrows">
                    <Icon
                      name="angle left"
                      className={currentPageValue === 1 ? "disabled" : null}
                      onClick={() => setTablePage("back")}
                    />
                    <Icon
                      name="angle right"
                      className={currentPageValue === pageNumbers.length ? "disabled" : null}
                      onClick={() => setTablePage("forward")}
                    />
                  </div>
                </div>
              )}
            </>
          }
        </div>
      ) : campaignsForMerge ? (
        <Accordion
          style={{ width: "100%" }}
          key={accordionKey ? accordionKey : 0}
          panels={renderItems()}
          styled
          exclusive={false}
          activeIndex={activeCampaignIndex}
          onTitleClick={(e, data) => handleCampaignClick(e, data)}
        />
      ) : (
        <p style={{ display: "flex", justifyContent: "center", marginTop: "5%" }}>
          {t("campaigns_no_search_results")}
        </p>
      )}
    </div>
  );
};

export default Campaigns;
